import { drawRaster, makeWorldCanvas, rasterWidth, rasterHeight } from '@/engine/canvas';
import { ctxOf, INTERNAL_W, INTERNAL_H } from '@/engine/canvas';
import { TILE } from '@/gfx/tiles';
import { visibility, drawLight, tracePolygon, lightAlpha, hexToRgb, type LightSource, type VisResult } from '@/gfx/lighting';
import { pointInPolygon, type Seg } from '@/engine/math';
import type { Floor } from '@/world/builder';
import { terrainCanvas } from './render';
import { ensureMemory } from '@/world/builder';

/**
 * Composites one frame of the world: remembered geometry (dim) + currently lit geometry + overlays.
 */
export class View {
  world = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  wctx = ctxOf(this.world);
  lightmap = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  lctx = ctxOf(this.lightmap);
  lit = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  litctx = ctxOf(this.lit);
  mem = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  mctx = ctxOf(this.mem);
  /** lights that only count where the viewer actually has line of sight */
  slights = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  sctx = ctxOf(this.slights);
  remembered = makeWorldCanvas(INTERNAL_W, INTERNAL_H);
  rctx = ctxOf(this.remembered);
  surface = makeWorldCanvas(128, 96);
  surfctx = ctxOf(this.surface);
  camX = 0;
  camY = 0;
  visResults: VisResult[] = [];
  viewerVis: VisResult | null = null;
  private visPool: VisResult[] = [];
  private clippedMemory: Array<{ light: LightSource; vis: VisResult; floor: Floor }> = [];
  /** Every light of the frame, so points can be sampled on the CPU instead of reading the lightmap back. */
  private frameLights: Array<{ light: LightSource; vis: VisResult; layer: 'main' | 'clipped' }> = [];
  /** Debug aid: when set, every sampleLight call is recorded as [x, y, r, g, b, a]. */
  recordSamples = false;
  samples: number[][] = [];

  beginFrame(f: Floor, camX: number, camY: number): void {
    this.camX = camX;
    this.camY = camY;
    ensureMemory(f);
    const w = this.wctx;
    w.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    drawRaster(w, terrainCanvas(f), camX, camY, INTERNAL_W, INTERNAL_H, 0, 0, INTERNAL_W, INTERNAL_H);
    this.rctx.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    drawRaster(this.rctx, terrainCanvas(f), camX, camY, INTERNAL_W, INTERNAL_H, 0, 0, INTERNAL_W, INTERNAL_H);
    this.lctx.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    this.sctx.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    this.visResults.length = 0;
    this.viewerVis = null;
    this.clippedMemory.length = 0;
    this.frameLights.length = 0;
    this.samples.length = 0;
  }

  private allocVis(): VisResult {
    let v = this.visPool[this.visResults.length];
    if (!v) {
      v = { pts: new Float32Array(4096), n: 0 };
      this.visPool.push(v);
    }
    return v;
  }

  /**
   * Add a light. `layer: 'clipped'` means the light is only shown where the viewer has line of
   * sight to it, so a lamp burning behind a wall does not light the wall for the player.
   */
  addLight(light: LightSource, segs: Seg[], remember: boolean, f: Floor, layer: 'main' | 'clipped' = 'main'): VisResult {
    const vis = visibility(light.x, light.y, light.r, segs, light.arc, this.allocVis());
    this.visResults.push(vis);
    this.frameLights.push({ light, vis, layer });
    drawLight(layer === 'clipped' ? this.sctx : this.lctx, light, vis, this.camX, this.camY);
    if (remember && layer === 'clipped') this.clippedMemory.push({ light, vis, floor: f });
    else if (remember) this.rememberLight(light, vis, f);
    return vis;
  }

  private rememberLight(light: LightSource, vis: VisResult, f: Floor): void {
    const mctx = ctxOf(f.memory);
    // partial alpha: a glimpse leaves a faint trace, a long look a solid one
    mctx.fillStyle = 'rgba(255,255,255,0.5)';
    tracePolygon(mctx, vis, 0, 0);
    if (light.arc) {
      mctx.save();
      mctx.clip();
      mctx.beginPath();
      mctx.moveTo(light.x, light.y);
      mctx.arc(light.x, light.y, light.r * 0.97, light.arc.center - light.arc.half, light.arc.center + light.arc.half);
      mctx.closePath();
      mctx.fill();
      mctx.restore();
    } else {
      mctx.fill();
    }
  }

  /** Mask the clipped light layer by the viewer's own visibility polygon and merge it in. */
  finishLights(los: VisResult | null): void {
    this.viewerVis = los;
    const s = this.sctx;
    if (los) {
      s.globalCompositeOperation = 'destination-in';
      s.fillStyle = '#fff';
      tracePolygon(s, los, this.camX, this.camY);
      s.fill();
      s.globalCompositeOperation = 'source-over';
      // Lightning can illuminate an adjacent room without making that room
      // visible to the player. Exploration must use the same viewer clip.
      for (const { light, vis, floor } of this.clippedMemory) {
        const memory = ctxOf(floor.memory);
        memory.save();
        tracePolygon(memory, los, 0, 0);
        memory.clip();
        this.rememberLight(light, vis, floor);
        memory.restore();
      }
    }
    this.clippedMemory.length = 0;
    this.lctx.globalCompositeOperation = 'lighter';
    drawRaster(this.lctx, this.slights, 0, 0);
    this.lctx.globalCompositeOperation = 'source-over';
  }

  /**
   * The light arriving at a world point, as the lightmap holds it after finishLights: every
   * light adds its premultiplied colour inside its own visibility polygon ('lighter'), clipped
   * lights only where the viewer can see, and the result is unpremultiplied like getImageData.
   * Evaluating this on the CPU avoids reading the GPU lightmap back every frame, which stalls
   * the whole pipeline on mobile GPUs. Call after finishLights and before any lightSurface.
   */
  sampleLight(x: number, y: number, out: Uint8ClampedArray = new Uint8ClampedArray(4)): Uint8ClampedArray {
    let r = 0, g = 0, b = 0, a = 0;
    const los = this.viewerVis;
    for (const { light, vis, layer } of this.frameLights) {
      const al = lightAlpha(light, x, y);
      if (al <= 0 || !pointInPolygon(x, y, vis.pts, vis.n)) continue;
      if (layer === 'clipped' && los && !pointInPolygon(x, y, los.pts, los.n)) continue;
      const [lr, lg, lb] = hexToRgb(light.color);
      r += lr * al; g += lg * al; b += lb * al; a += al;
    }
    a = Math.min(1, a);
    out[0] = a > 0 ? Math.min(255, r) / a : 0;
    out[1] = a > 0 ? Math.min(255, g) / a : 0;
    out[2] = a > 0 ? Math.min(255, b) / a : 0;
    out[3] = a * 255;
    if (this.recordSamples) this.samples.push([x, y, out[0], out[1], out[2], out[3]]);
    return out;
  }

  /** Lift floor-plane illumination onto a visible upright sprite's face. */
  lightSurface(sprite: HTMLCanvasElement, x: number, y: number, rgba: Uint8ClampedArray): void {
    if (rgba[3] < 5) return;
    const s = this.surfctx;
    s.clearRect(0, 0, this.surface.width, this.surface.height);
    drawRaster(s, sprite, 0, 0);
    s.globalCompositeOperation = 'source-in';
    s.fillStyle = `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${rgba[3] / 255 * 0.85})`;
    s.fillRect(0, 0, rasterWidth(sprite), rasterHeight(sprite));
    s.globalCompositeOperation = 'source-over';
    drawRaster(this.lctx, this.surface, 0, 0, rasterWidth(sprite), rasterHeight(sprite), x, y, rasterWidth(sprite), rasterHeight(sprite));
  }

  /** Compose to the screen context. `dynamic` draws entities on the world canvas before lighting. */
  compose(ctx: CanvasRenderingContext2D, f: Floor, memoryTint: string): void {
    const { camX, camY } = this;
    // lit world = world masked by lightmap alpha, tinted by light colour
    // tint the world by the light colour first, then let the light's alpha decide how much of it
    // shows. Doing the alpha step last keeps brightness exactly proportional to the light.
    const l = this.litctx;
    l.globalCompositeOperation = 'source-over';
    l.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    drawRaster(l, this.world, 0, 0);
    l.globalCompositeOperation = 'multiply';
    drawRaster(l, this.lightmap, 0, 0);
    l.globalCompositeOperation = 'destination-in';
    drawRaster(l, this.lightmap, 0, 0);
    l.globalCompositeOperation = 'source-over';

    // memory layer = static masked by memory, dimmed
    const m = this.mctx;
    m.globalCompositeOperation = 'source-over';
    m.clearRect(0, 0, INTERNAL_W, INTERNAL_H);
    drawRaster(m, this.remembered, 0, 0);
    m.globalCompositeOperation = 'source-atop';
    m.fillStyle = memoryTint;
    m.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
    // blur the remembered mask so explored ground fades out instead of ending in hard steps
    m.globalCompositeOperation = 'destination-in';
    m.filter = 'blur(3px)';
    drawRaster(m, f.memory, camX, camY, INTERNAL_W, INTERNAL_H, 0, 0, INTERNAL_W, INTERNAL_H);
    m.filter = 'none';
    // Feather inward only: a blurred memory edge must never reveal an unseen room.
    drawRaster(m, f.memory, camX, camY, INTERNAL_W, INTERNAL_H, 0, 0, INTERNAL_W, INTERNAL_H);
    m.globalCompositeOperation = 'source-over';

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#020108';
    ctx.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
    drawRaster(ctx, this.mem, 0, 0);
    drawRaster(ctx, this.lit, 0, 0);
  }
}

export function worldToScreen(view: View, x: number, y: number): [number, number] {
  return [Math.round(x - view.camX), Math.round(y - view.camY)];
}

export function clampCamera(f: Floor, cx: number, cy: number): [number, number] {
  const fw = f.w * TILE, fh = f.h * TILE;
  let x = Math.round((cx - INTERNAL_W / 2) * 2) / 2, y = Math.round((cy - INTERNAL_H / 2) * 2) / 2;
  x = Math.max(0, Math.min(fw - INTERNAL_W, x));
  y = Math.max(0, Math.min(fh - INTERNAL_H, y));
  return [x, y];
}
